/* TH modules · shared behaviour.
   - <th-stock-scarcity>: refreshes the stock message when the theme publishes
     variantChange (theme.pubsub), from the section HTML the theme already fetched.
   - <th-cart-notice>: countdown for the cart notice. One timer and one set of
     listeners per page, however many copies of the notice are rendered or
     re-rendered (cart updates, theme editor).
   assets/theme.js and assets/cart.js are not modified: only their events are used. */
(function () {
  'use strict';

  if (window.thComponents) return;

  var TH = (window.thComponents = { version: '1.0.0' });

  /* ---------- shared helpers ---------- */

  var storage = {
    get: function (key) {
      try { return window.localStorage.getItem(key); } catch (error) { return null; }
    },
    set: function (key, value) {
      try { window.localStorage.setItem(key, value); } catch (error) { /* private mode */ }
    },
    remove: function (key) {
      try { window.localStorage.removeItem(key); } catch (error) { /* private mode */ }
    }
  };

  var liveRegion = null;

  function announce(text) {
    if (!text || !document.body) return;
    if (!liveRegion || !liveRegion.isConnected) {
      liveRegion = document.createElement('div');
      liveRegion.setAttribute('aria-live', 'polite');
      liveRegion.setAttribute('data-th-live', '');
      liveRegion.style.cssText = 'position:absolute;width:1px;height:1px;margin:-1px;padding:0;overflow:hidden;clip:rect(0 0 0 0);white-space:nowrap;border:0;';
      document.body.appendChild(liveRegion);
    }
    liveRegion.textContent = '';
    window.setTimeout(function () { liveRegion.textContent = text; }, 50);
  }

  function pad(number) {
    return number < 10 ? '0' + number : String(number);
  }

  function formatTime(totalSeconds) {
    var seconds = Math.max(0, totalSeconds);
    return pad(Math.floor(seconds / 60)) + ':' + pad(seconds % 60);
  }

  function subscribe(eventName, callback) {
    var pubsub = window.theme && window.theme.pubsub;
    if (!pubsub || typeof pubsub.subscribe !== 'function') return function () {};
    var name = (pubsub.PUB_SUB_EVENTS && pubsub.PUB_SUB_EVENTS[eventName]) || eventName;
    return pubsub.subscribe(name, callback);
  }

  TH.storage = storage;
  TH.announce = announce;
  TH.formatTime = formatTime;

  /* ---------- TH · Stock Scarcity ---------- */

  class ThStockScarcity extends HTMLElement {
    connectedCallback() {
      if (this.unsubscribe) return;
      var element = this;
      this.unsubscribe = subscribe('variantChange', function (event) {
        var data = event && event.data;
        if (!data || !data.parsedHTML) return;
        if (String(data.sectionId) !== element.dataset.sectionId) return;
        if (String(data.productId) !== element.dataset.productId) return;
        element.refreshFrom(data.parsedHTML);
      });
    }

    disconnectedCallback() {
      if (this.unsubscribe) {
        this.unsubscribe();
        this.unsubscribe = null;
      }
    }

    /* The section HTML for the newly selected variant is rendered by Liquid with the
       same rules as the first page load, so the node is copied as is. */
    refreshFrom(doc) {
      var source = doc.getElementById(this.id);
      if (!source) {
        this.hidden = true;
        return;
      }
      if (this.innerHTML !== source.innerHTML) this.innerHTML = source.innerHTML;
      if (source.hasAttribute('hidden')) this.setAttribute('hidden', '');
      else this.removeAttribute('hidden');
      this.dataset.variantId = source.dataset.variantId || '';
      // look mode and message kind come from the server too (Custom Liquid styling hooks)
      ['data-mode', 'data-th-kind'].forEach(function (name) {
        if (source.hasAttribute(name)) this.setAttribute(name, source.getAttribute(name));
        else this.removeAttribute(name);
      }, this);
    }
  }

  /* ---------- TH · Cart Notice ---------- */

  var CART_KEY = 'th_cart_notice_started_at';
  // Key the Vitals Cart Notice uses for the same purpose: a start time in ms.
  var VITALS_KEY = '__v_cr__s_t__';

  var notice = {
    instances: new Set(),
    config: null,
    deadline: 0,
    interval: 0,
    ended: false,
    emptied: false,
    desktopDismissed: false
  };

  function readConfig(element) {
    var data = element.dataset;
    return {
      minutes: Math.max(1, parseInt(data.duration, 10) || 10),
      restart: data.restart !== 'false',
      keepDesktop: data.keepDesktop === 'true',
      migrateVitals: data.migrateVitals !== 'false',
      endedMessage: data.endedMessage || ''
    };
  }

  /* Vitals stores a plain millisecond timestamp. Anything else (missing, malformed,
     in the future or older than a day) is ignored and the countdown starts fresh. */
  function readVitalsStart() {
    var raw = storage.get(VITALS_KEY);
    if (!raw) return 0;
    var value = Number(raw);
    if (!isFinite(value)) {
      try {
        var parsed = JSON.parse(raw);
        value = Number(parsed && typeof parsed === 'object' ? (parsed.value != null ? parsed.value : parsed.v) : parsed);
      } catch (error) {
        value = NaN;
      }
    }
    var now = Date.now();
    if (!isFinite(value) || value > now + 60000 || value < now - 86400000) return 0;
    return value;
  }

  function remainingSeconds() {
    return Math.ceil((notice.deadline - Date.now()) / 1000);
  }

  function stopTimer() {
    if (notice.interval) window.clearInterval(notice.interval);
    notice.interval = 0;
    notice.deadline = 0;
  }

  function startTimer(config) {
    if (notice.deadline || notice.ended || notice.emptied) return;

    var duration = config.minutes * 60000;
    var now = Date.now();
    var started = parseInt(storage.get(CART_KEY), 10);

    if (!(started > 0)) {
      started = (config.migrateVitals && readVitalsStart()) || now;
      storage.set(CART_KEY, String(started));
    }
    if (started > now) {
      started = now;
      storage.set(CART_KEY, String(started));
    }
    if (now >= started + duration) {
      // Same as Vitals: an expired countdown starts again on the next cart visit.
      if (!config.restart) {
        endTimer(false);
        return;
      }
      started = now;
      storage.set(CART_KEY, String(started));
    }

    notice.deadline = started + duration;
    tick();
    notice.interval = window.setInterval(tick, 1000);
  }

  function tick() {
    if (!notice.deadline) return;
    // Shows 00:00 for one tick, then hides, like Vitals.
    var seconds = remainingSeconds();
    if (seconds < 0) {
      endTimer(true);
      return;
    }
    var text = formatTime(seconds);
    notice.instances.forEach(function (element) { element.setTime(text); });
  }

  /* 0:00 — the notice disappears (or, in Custom Liquid mode with a non-empty "after
     0:00" template, switches to that template); the cart is not touched. The stored
     start stays, so the next visit starts again when restart is on. */
  function endTimer(withAnnouncement) {
    var wasVisible = false;
    stopTimer();
    notice.ended = true;
    notice.instances.forEach(function (element) {
      if (element.isVisible()) wasVisible = true;
      element.showEnded();
    });
    if (withAnnouncement && wasVisible && notice.config) announce(notice.config.endedMessage);
  }

  function onCartChanged(cart) {
    if (!notice.instances.size) return;
    if (cart && cart.item_count === 0) {
      // Empty cart: hide and forget the countdown (Vitals behaviour).
      stopTimer();
      storage.remove(CART_KEY);
      notice.emptied = true;
      notice.instances.forEach(function (element) { element.hidden = true; });
      return;
    }
    if (notice.config && !notice.config.keepDesktop) {
      // Former Vitals behaviour: on desktop the notice is gone after a cart update
      // until the page is reloaded.
      notice.desktopDismissed = true;
      notice.instances.forEach(function (element) {
        if (element.dataset.placement === 'desktop') element.hidden = true;
      });
    }
  }

  document.addEventListener('cart:updated', function (event) {
    onCartChanged(event && event.detail && event.detail.cart);
  });

  // Theme editor: a re-rendered cart section starts from a clean state.
  document.addEventListener('shopify:section:unload', function (event) {
    if (!event.target || !event.target.querySelector || !event.target.querySelector('th-cart-notice')) return;
    stopTimer();
    notice.ended = false;
    notice.emptied = false;
    notice.desktopDismissed = false;
  });

  class ThCartNotice extends HTMLElement {
    connectedCallback() {
      notice.instances.add(this);
      notice.config = readConfig(this);

      if (notice.emptied || (this.dataset.placement === 'desktop' && notice.desktopDismissed)) {
        this.hidden = true;
        return;
      }
      if (notice.ended) {
        this.showEnded();
        return;
      }

      startTimer(notice.config);
      if (notice.deadline) this.setTime(formatTime(remainingSeconds()));
      else if (notice.ended) this.showEnded();
    }

    /* After 0:00: the "after 0:00" Custom Liquid template if there is one with
       content, otherwise hidden (standard behaviour). Idempotent. */
    showEnded() {
      var template = this.querySelector('template[data-th-cn-ended]');
      var content = template && template.content;
      var hasContent = !!content && (content.textContent.trim() !== '' || !!content.querySelector('*'));
      if (!hasContent) {
        this.hidden = true;
        return;
      }
      if (this.getAttribute('data-state') !== 'ended') {
        var view = document.createElement('div');
        view.className = 'th-cart-notice__custom';
        view.setAttribute('data-th-cn-ended-view', '');
        view.appendChild(content.cloneNode(true));
        var running = this.querySelector('[data-th-cn-running]');
        if (running) running.replaceWith(view);
        else this.insertBefore(view, template);
        this.setAttribute('data-state', 'ended');
      }
      this.hidden = false;
    }

    disconnectedCallback() {
      notice.instances.delete(this);
      if (!notice.instances.size) stopTimer();
    }

    setTime(text) {
      var timers = this.querySelectorAll('[data-th-cn-timer]');
      for (var index = 0; index < timers.length; index++) {
        if (timers[index].textContent !== text) timers[index].textContent = text;
      }
      if (!this.hasAttribute('data-ready')) this.setAttribute('data-ready', '');
    }

    isVisible() {
      return !this.hidden && this.getClientRects().length > 0;
    }
  }

  /* ---------- TH · Product Badges ---------- */

  // Card controls and labels that sit on the product image and must stay fully visible.
  var BADGE_OBSTACLES = '.quick-add .button, .quick-view__button, .product-card__rating, .badges > .badge';
  var BADGE_GAP = 6;

  // Layout box of node relative to media, ignoring CSS transforms, plus how far a
  // resting transform moves it (quick add slides 8px down until hover on desktop).
  // Controls hidden with visibility/opacity still count: the theme shows them on
  // hover or focus. Only controls that are not rendered at all are skipped.
  function layoutBox(node, media) {
    if (!node.offsetWidth || !node.offsetHeight) return null;
    var x = 0;
    var y = 0;
    var rest = 0;
    var element = node;
    while (element && element !== media) {
      x += element.offsetLeft;
      y += element.offsetTop;
      element = element.offsetParent;
    }
    if (element !== media) return null;
    for (var up = node; up && up !== media; up = up.parentElement) {
      var transform = window.getComputedStyle(up).transform;
      if (transform && transform !== 'none') rest += new DOMMatrixReadOnly(transform).m42;
    }
    return {
      left: x,
      right: x + node.offsetWidth,
      top: Math.min(y, y + rest),
      bottom: Math.max(y + node.offsetHeight, y + node.offsetHeight + rest)
    };
  }

  class ThBadges extends HTMLElement {
    connectedCallback() {
      var media = this.parentElement;
      if (!media || !('ResizeObserver' in window)) return;
      var element = this;
      this.media = media;
      this.observer = new ResizeObserver(function () { element.schedule(); });
      this.observer.observe(media);
      this.observer.observe(this);
      this.watchObstacles();
      // controls or labels added to the card later (apps, tests, re-renders)
      this.mutations = new MutationObserver(function () {
        element.watchObstacles();
        element.schedule();
      });
      this.mutations.observe(media, { childList: true, subtree: true });
      this.schedule();
    }

    watchObstacles() {
      var observer = this.observer;
      this.media.querySelectorAll(BADGE_OBSTACLES).forEach(function (node) { observer.observe(node); });
    }

    disconnectedCallback() {
      if (this.mutations) this.mutations.disconnect();
      this.mutations = null;
      if (this.observer) this.observer.disconnect();
      if (this.frame) window.cancelAnimationFrame(this.frame);
      this.observer = null;
      this.frame = 0;
    }

    schedule() {
      if (this.frame) return;
      var element = this;
      this.frame = window.requestAnimationFrame(function () {
        element.frame = 0;
        element.place();
      });
    }

    /* Moves the group up (bottom corners) or down (top corners) just enough to clear
       every control it would meet; nothing moves where nothing meets. Hidden cards
       (closed menus, filtered grids) are placed when they get a size. */
    place() {
      var media = this.media;
      if (!media || !this.offsetWidth || !media.offsetWidth) return;
      var fromBottom = (this.dataset.position || 'bottom-right').indexOf('bottom') === 0;
      var group = { left: this.offsetLeft, right: this.offsetLeft + this.offsetWidth, top: this.offsetTop, bottom: this.offsetTop + this.offsetHeight };
      var boxes = [];
      var nodes = media.querySelectorAll(BADGE_OBSTACLES);
      for (var index = 0; index < nodes.length; index++) {
        var box = layoutBox(nodes[index], media);
        if (box && box.right + BADGE_GAP > group.left && box.left - BADGE_GAP < group.right) boxes.push(box);
      }
      var shift = 0;
      for (var pass = 0; pass < 4; pass++) {
        var moved = false;
        for (var k = 0; k < boxes.length; k++) {
          var b = boxes[k];
          var top = group.top + (fromBottom ? -shift : shift);
          var bottom = group.bottom + (fromBottom ? -shift : shift);
          if (b.top < bottom + BADGE_GAP && b.bottom > top - BADGE_GAP) {
            shift = Math.max(shift, fromBottom ? group.bottom - b.top + BADGE_GAP : b.bottom - group.top + BADGE_GAP);
            moved = true;
          }
        }
        if (!moved) break;
      }
      // keep the badge on the image
      var room = fromBottom ? group.top : media.offsetHeight - group.bottom;
      shift = Math.max(0, Math.min(shift, room));
      var value = (fromBottom ? -shift : shift) + 'px';
      if (this.style.getPropertyValue('--th-badges-shift') !== value) this.style.setProperty('--th-badges-shift', value);
      this.toggleAttribute('data-shifted', shift > 0);
    }
  }

  TH.cartNotice = {
    state: notice,
    storageKey: CART_KEY,
    vitalsKey: VITALS_KEY
  };

  if (!window.customElements.get('th-stock-scarcity')) {
    window.customElements.define('th-stock-scarcity', ThStockScarcity);
  }
  if (!window.customElements.get('th-cart-notice')) {
    window.customElements.define('th-cart-notice', ThCartNotice);
  }
  if (!window.customElements.get('th-badges')) {
    window.customElements.define('th-badges', ThBadges);
  }
})();
